/**
 * Builds the "Simple emails" option list for SendEmailView.
 *
 * Two contexts share this module:
 * - 'booking': the booking drawer, one booking, matches the drawer's original
 *   behavior (single-booking confirmation and payment request payloads).
 * - 'transaction': the transaction details page, aggregated across the page's
 *   mixed rows array (bookings, items, gift cards). Transaction-scoped emails
 *   appear once; booking-scoped emails fan out per eligible booking; gift card
 *   delivery fans out per active gift card and goes to the card recipient.
 *
 * Every option carries the exact endpoint suffix and POST body so the view can
 * send generically. Payload shapes mirror the transaction page handlers
 * (resendConfirmationEmail, sendReminderEmail, sendFollowUpEmail,
 * sendPaymentRequest, sendGiftCardEmail in TransactionDetails.vue).
 */

const DESCRIPTIONS = {
  confirmation: 'Resend the standard booking confirmation email.',
  reminder: 'Send the reminder email configured in your automated emails.',
  follow_up: 'Send the post-event follow-up email.',
  payment_request: 'Send a request to collect the outstanding balance.',
  gift_card_confirmation: 'Send the purchase confirmation for the gift cards on this transaction.',
  gift_card_delivery: 'Send the gift card to its recipient.'
}

const NO_CUSTOMER_EMAIL = 'No customer email'
const NO_BALANCE_DUE = 'No balance due'
const NO_RECIPIENT_EMAIL = 'No recipient email'

export function isEmailSettingActive(emailSettings, emailType) {
  const setting = (emailSettings || []).find(s => s && s.email_type === emailType)
  return !!(setting && (setting.is_active === true || setting.is_active === 1))
}

function toDue(value) {
  const n = parseFloat(value)
  return Number.isFinite(n) ? n : 0
}

function option(fields) {
  const disabled = !!fields.disabled
  return {
    kind: 'simple',
    ...fields,
    disabled,
    disabledReason: disabled ? (fields.disabledReason || null) : null
  }
}

function customerGate(recipient, extra) {
  if (!recipient) return { disabled: true, disabledReason: NO_CUSTOMER_EMAIL }
  if (extra && extra.disabled) return extra
  return { disabled: false, disabledReason: null }
}

function buildBookingContext({ booking, emailSettings }) {
  const b = booking || {}
  const email = b.customer_email || b.email || ''
  const out = []

  if (isEmailSettingActive(emailSettings, 'confirmation')) {
    out.push(option({
      key: 'simple:confirmation',
      email_type: 'confirmation',
      name: 'Booking confirmation',
      description: DESCRIPTIONS.confirmation,
      recipient: email,
      endpoint: '/confirmation',
      body: { email, bookings: [b] },
      ...customerGate(email)
    }))
  }
  if (isEmailSettingActive(emailSettings, 'reminder')) {
    out.push(option({
      key: 'simple:reminder',
      email_type: 'reminder',
      name: 'Reminder',
      description: DESCRIPTIONS.reminder,
      recipient: email,
      endpoint: '/reminder',
      body: { email, booking_id: b.id },
      ...customerGate(email)
    }))
  }
  if (isEmailSettingActive(emailSettings, 'follow_up')) {
    out.push(option({
      key: 'simple:follow_up',
      email_type: 'follow_up',
      name: 'Follow-up',
      description: DESCRIPTIONS.follow_up,
      recipient: email,
      endpoint: '/follow-up',
      body: { email, booking_id: b.id },
      ...customerGate(email)
    }))
  }
  if (isEmailSettingActive(emailSettings, 'payment_request')) {
    const dueGate = toDue(b.due) > 0 ? null : { disabled: true, disabledReason: NO_BALANCE_DUE }
    out.push(option({
      key: 'simple:payment_request',
      email_type: 'payment_request',
      name: 'Payment request',
      description: DESCRIPTIONS.payment_request,
      recipient: email,
      endpoint: '/payments/request',
      body: { email, bookings: [b] },
      ...customerGate(email, dueGate)
    }))
  }
  return out
}

function parseRecipient(raw) {
  if (!raw) return null
  try {
    const arr = typeof raw === 'string' ? JSON.parse(raw) : raw
    return Array.isArray(arr) && arr.length > 0 ? arr[0] : null
  } catch (e) {
    return null
  }
}

function bookingLabel(prefix, booking, formatDate, formatTime) {
  const name = booking.name || ('Booking #' + booking.id)
  const date = formatDate ? formatDate(booking.booking_date) : booking.booking_date
  const time = formatTime ? formatTime(booking.start_time) : booking.start_time
  return `${prefix}: ${name}, ${[date, time].filter(Boolean).join(' ')}`
}

function buildTransactionContext({ transaction, rows, emailSettings, formatDate, formatTime }) {
  const tx = transaction || {}
  const list = Array.isArray(rows) ? rows : []
  const email = tx.email || ''
  const active = tx.status === 1
  const out = []

  const bookingRows = list.filter(r => r && Object.prototype.hasOwnProperty.call(r, 'booking_date') && !Object.prototype.hasOwnProperty.call(r, 'design_url'))
  const eligibleBookings = bookingRows.filter(r => r.status !== 'cancelled' && r.status !== 'requested')
  const giftCardRows = list.filter(r => r && Object.prototype.hasOwnProperty.call(r, 'design_url') && !!r.is_active)

  if (active && eligibleBookings.length > 0 && isEmailSettingActive(emailSettings, 'confirmation')) {
    out.push(option({
      key: 'simple:confirmation',
      email_type: 'confirmation',
      name: 'Booking confirmation',
      description: DESCRIPTIONS.confirmation,
      recipient: email,
      endpoint: '/confirmation',
      body: { email, bookings: list },
      ...customerGate(email)
    }))
  }

  if (active && isEmailSettingActive(emailSettings, 'reminder')) {
    for (const b of eligibleBookings) {
      out.push(option({
        key: `simple:reminder:${b.id}`,
        email_type: 'reminder',
        name: bookingLabel('Reminder', b, formatDate, formatTime),
        description: DESCRIPTIONS.reminder,
        recipient: email,
        endpoint: '/reminder',
        body: { email, booking_id: b.id },
        ...customerGate(email)
      }))
    }
  }

  if (active && isEmailSettingActive(emailSettings, 'follow_up')) {
    for (const b of eligibleBookings) {
      out.push(option({
        key: `simple:follow_up:${b.id}`,
        email_type: 'follow_up',
        name: bookingLabel('Follow-up', b, formatDate, formatTime),
        description: DESCRIPTIONS.follow_up,
        recipient: email,
        endpoint: '/follow-up',
        body: { email, booking_id: b.id },
        ...customerGate(email)
      }))
    }
  }

  if (active && isEmailSettingActive(emailSettings, 'payment_request')) {
    const dueGate = toDue(tx.due) > 0 ? null : { disabled: true, disabledReason: NO_BALANCE_DUE }
    out.push(option({
      key: 'simple:payment_request',
      email_type: 'payment_request',
      name: 'Payment request',
      description: DESCRIPTIONS.payment_request,
      recipient: email,
      endpoint: '/payments/request',
      body: { email, bookings: list },
      ...customerGate(email, dueGate)
    }))
  }

  if (active && giftCardRows.length > 0 && isEmailSettingActive(emailSettings, 'gift_card_confirmation')) {
    out.push(option({
      key: 'simple:gift_card_confirmation',
      email_type: 'gift_card_confirmation',
      name: 'Gift card purchase confirmation',
      description: DESCRIPTIONS.gift_card_confirmation,
      recipient: email,
      endpoint: '/confirmation',
      body: { email, bookings: list, target: 'gift_card' },
      ...customerGate(email)
    }))
  }

  if (isEmailSettingActive(emailSettings, 'gift_cards')) {
    for (const card of giftCardRows) {
      const recipientObj = parseRecipient(card.recipients)
      const recipient = (recipientObj && recipientObj.email) || ''
      const who = recipientObj
        ? [recipientObj.first_name, recipientObj.last_name].filter(Boolean).join(' ')
        : ''
      out.push(option({
        key: `simple:gift_card_delivery:${card.id}`,
        email_type: 'gift_card_delivery',
        name: `Gift card delivery: ${who || card.name || ('#' + card.id)}`,
        description: DESCRIPTIONS.gift_card_delivery,
        recipient,
        endpoint: '/gift-cards',
        body: { email: recipient, gift_card_id: card.id },
        disabled: !recipient,
        disabledReason: recipient ? null : NO_RECIPIENT_EMAIL
      }))
    }
  }

  return out
}

/**
 * @param {Object} input
 * @param {'booking'|'transaction'} input.context
 * @param {Object} [input.booking]        booking context: the single booking
 * @param {Object} [input.transaction]    transaction context: { id, email, status, due }
 * @param {Array}  [input.rows]           transaction context: mixed bookings/items/gift cards
 * @param {Array}  input.emailSettings    [{ email_type, is_active }]
 * @param {Function} [input.formatDate]   (dateString) => string, bound to the group's date format
 * @param {Function} [input.formatTime]   (timeString) => string, bound to the group's time format
 */
export function buildSimpleEmailOptions(input) {
  const ctx = input && input.context
  if (ctx === 'transaction') return buildTransactionContext(input || {})
  return buildBookingContext(input || {})
}
